import {
  AlertCircle,
  Building2,
  CalendarPlus,
  Check,
  Globe,
  History,
  Loader2,
  Pencil,
  Plus,
  Shield,
  Ticket,
  Trash2,
  UserCog,
  Users,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { AppShell, SectionLabel } from "components/AppShell";
import { PageTabPanel, PageTabs, type PageTab } from "components/PageTabs";
import { EmptyState, LoadingCards } from "components/states";
import { ToggleChip } from "components/ToggleChip";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";

import { EventDeletionDangerZone } from "../events/EventDeletionControl";
import { TimezonePicker } from "../events/TimezonePicker";
import { eventTimezones, timezoneReferenceDate } from "../events/timezones";
import { eventIdFromTitle } from "./event-id";
import { GovernanceCommandFeedback } from "./GovernanceCommandFeedback";
import { Notice } from "./Notice";

import { AttendeesSection } from "./AttendeesSection";
import { GovernanceEventsSection } from "./GovernanceEventsSection";
import { GovernanceInvitesSection } from "./GovernanceInvitesSection";
import { AuditLogSection } from "./AuditLogSection";
import { BUILT_IN_DOMAINS } from "./built-in-domains";
import { describeReadFailure } from "./describe-read-failure";
import { searchMatcher } from "utils/search";
import { compareUsers, ROLE_LABELS, userSearchFields } from "./users-list";
import {
  useBusinessUnits,
  type GovernanceBusinessUnit,
} from "./use-business-units";
import { useBusinessUnitWrites } from "./use-business-unit-writes";
import { useDomainAllowlist } from "./use-domain-allowlist";
import {
  useGovernanceCommand,
  type GovernanceCommandState,
} from "./use-governance-command";
import { useEventAdmins, type EventAdminsState } from "./use-event-admins";
import { useGovernanceEvents } from "./use-governance-events";
import { useGovernanceUsers } from "./use-governance-users";
import type { GovernanceEvent, GovernanceUser } from "./schemas";
import {
  ATTENDEES_MODULE_HELP,
  ATTENDEES_MODULE_LABEL,
} from "../admin/module-labels";

// `closed` is gone with the last closed capability. Every governance tab now
// has a backend command behind it; the padlock and its placeholder section
// went with it rather than lingering as scaffolding nothing renders.
const TABS: PageTab<string>[] = [
  { key: "events", label: "Events", icon: CalendarPlus },
  { key: "domains", label: "Domains", icon: Globe },
  { key: "users", label: "Users", icon: UserCog },
  { key: "admins", label: "Event Admins", icon: Shield },
  { key: "audit", label: "Audit Log", icon: History },
  { key: "attendees", label: "Attendees", icon: Users },
  { key: "units", label: "Business Units", icon: Building2 },
  { key: "invites", label: "Invites", icon: Ticket },
];

export function GovernancePage({
  uid,
  email,
  signOut,
}: {
  uid: string;
  email: string | null;
  signOut: () => void;
}) {
  const [activeTab, setActiveTab] = useState("units");

  return (
    <AppShell
      identity={{ email }}
      onSignOut={signOut}
      title="Platform Governance"
      actions={
        <Badge variant="outline" className="rounded-full">
          Super Admin
        </Badge>
      }
    >
      <PageTabs
        tabs={TABS}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        idPrefix="governance-tabs"
        testId="governance-tabs"
      />

      <PageTabPanel
        idPrefix="governance-tabs"
        tabKey="events"
        active={activeTab === "events"}
      >
        {activeTab === "events" && <EventsTab uid={uid} />}
      </PageTabPanel>
      <PageTabPanel
        idPrefix="governance-tabs"
        tabKey="domains"
        active={activeTab === "domains"}
      >
        {activeTab === "domains" && <DomainsSection uid={uid} />}
      </PageTabPanel>
      <PageTabPanel
        idPrefix="governance-tabs"
        tabKey="users"
        active={activeTab === "users"}
      >
        {activeTab === "users" && <UsersSection uid={uid} />}
      </PageTabPanel>
      <PageTabPanel
        idPrefix="governance-tabs"
        tabKey="admins"
        active={activeTab === "admins"}
      >
        {activeTab === "admins" && <EventAdminsSection uid={uid} />}
      </PageTabPanel>
      <PageTabPanel
        idPrefix="governance-tabs"
        tabKey="audit"
        active={activeTab === "audit"}
      >
        {activeTab === "audit" && <AuditLogSection />}
      </PageTabPanel>
      <PageTabPanel
        idPrefix="governance-tabs"
        tabKey="attendees"
        active={activeTab === "attendees"}
      >
        {activeTab === "attendees" && <AttendeesSection uid={uid} />}
      </PageTabPanel>
      <PageTabPanel
        idPrefix="governance-tabs"
        tabKey="units"
        active={activeTab === "units"}
      >
        {activeTab === "units" && <BusinessUnitsSection />}
      </PageTabPanel>
      <PageTabPanel
        idPrefix="governance-tabs"
        tabKey="invites"
        active={activeTab === "invites"}
      >
        {activeTab === "invites" && <GovernanceInvitesSection uid={uid} />}
      </PageTabPanel>
    </AppShell>
  );
}

// Refetch only once the backend trigger has actually applied the command.
// Reloading when submit() resolves would race it: submit resolves after the
// command document is written as REQUESTED, before the trigger has run.
function useReloadOnSuccess(
  status: GovernanceCommandState["status"],
  reload: () => void,
) {
  useEffect(() => {
    if (status === "succeeded") reload();
  }, [status, reload]);
}

// Common enough to be worth one click; anything else is typed. Not the full
// IANA list, which is ~600 entries and would need a combobox to be usable.
// Mirrors MODULE_DEFAULTS in the CREATE_EVENT service, which in turn mirrors
// the defaults isValidEvent applies to an absent key. All three have to agree.
const MODULE_DEFAULTS: Record<string, boolean> = {
  configAgenda: true,
  configSpeakers: true,
  configAttendees: true,
  configPartners: false,
  configVenues: false,
  configResources: false,
  configInformation: false,
  configNow: true,
  configAfterEvent: false,
  configConnections: false,
};

const MODULE_LABELS: Record<string, string> = {
  configAgenda: "Agenda",
  configSpeakers: "Speakers",
  configAttendees: ATTENDEES_MODULE_LABEL,
  configPartners: "Partners",
  configVenues: "Venues",
  configResources: "Resources",
  configInformation: "Info",
  configNow: "Now",
  configAfterEvent: "After the event",
  configConnections: "Connections",
};

/**
 * The create form, then every event with its delete control.
 *
 * Super admins may list `events` (firestore.rules, `match /events/{eventId}`),
 * so this tab shows events nobody has made the operator admin of, which is the
 * case that matters for deletion.
 */
function EventsTab({ uid }: { uid: string }) {
  const events = useGovernanceEvents();
  return (
    <div className="space-y-8">
      <NewEventSection uid={uid} onCreated={events.reload} />
      <section className="space-y-3">
        <SectionLabel>All events</SectionLabel>
        <GovernanceEventsSection uid={uid} events={events} />
      </section>
    </div>
  );
}

/**
 * The create-event form. Governance shows it above the list of every event;
 * the admin landing page shows it in a dialog.
 */
export function NewEventSection({
  uid,
  onCreated,
}: {
  uid: string;
  /** Called with the new event's id once the trigger has applied the command.
   *  Governance passes its list reload and keeps the "create another" panel.
   *  The admin landing page passes a handler that navigates into the event. */
  onCreated?: (eventId: string) => void;
}) {
  const { state: cmdState, submit, reset } = useGovernanceCommand();
  const [title, setTitle] = useState("");
  const [eventId, setEventId] = useState("");
  const [idEdited, setIdEdited] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  // The full IANA list starts in Africa; a new event defaults to home.
  const [timezone, setTimezone] = useState<string>("Europe/Berlin");
  // Offsets as they will be on the start date, or today until one is picked.
  const timezoneOptions = useMemo(
    () => eventTimezones(timezoneReferenceDate(startDate)),
    [startDate],
  );
  const [location, setLocation] = useState("");
  const [modules, setModules] = useState<Record<string, boolean>>(() => ({
    ...MODULE_DEFAULTS,
  }));

  const busy =
    cmdState.status === "submitting" || cmdState.status === "watching";
  const suggestedId = eventIdFromTitle(title);
  const effectiveId = idEdited ? eventId : suggestedId;

  // Pinned at submit rather than read at success, because the success panel
  // offers "Create another" and that clears the form's id.
  const createdIdRef = useRef("");
  const notifyCreated = useCallback(() => {
    if (createdIdRef.current) onCreated?.(createdIdRef.current);
  }, [onCreated]);
  useReloadOnSuccess(cmdState.status, notifyCreated);

  // Mirrors the CREATE_EVENT rule, so the button is disabled for exactly the
  // payloads the rules would reject rather than letting them round-trip.
  const datesOk =
    /^\d{4}-\d{2}-\d{2}$/.test(startDate) &&
    /^\d{4}-\d{2}-\d{2}$/.test(endDate) &&
    endDate >= startDate;
  const idOk = /^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]$/.test(effectiveId);
  const canSubmit =
    !busy && title.trim().length > 0 && idOk && datesOk && timezone.length > 0;

  const handleCreate = async () => {
    if (!canSubmit) return;
    createdIdRef.current = effectiveId;
    await submit("CREATE_EVENT", uid, {
      eventId: effectiveId,
      title: title.trim(),
      startDate,
      endDate,
      timezone,
      // Omitted when blank: the rules bound it with isBoundedString, which
      // refuses an empty string.
      ...(location.trim() ? { location: location.trim() } : {}),
      // Only switches the operator actually CHANGED. An event that stores no
      // flag follows isValidEvent's default, so sending all ten would freeze
      // today's defaults into every event and diverge the day one changes.
      ...Object.fromEntries(
        Object.entries(modules).filter(
          ([key, value]) => value !== MODULE_DEFAULTS[key],
        ),
      ),
    });
  };

  if (cmdState.status === "succeeded") {
    return (
      <div className="space-y-4" data-testid="events-content">
        <EmptyState icon={Check} title={`Created ${effectiveId}`}>
          It is unpublished and you are its event administrator. Open
          Administration to add the agenda, then publish it there.
        </EmptyState>
        <Button
          onClick={() => {
            reset();
            setTitle("");
            setEventId("");
            setIdEdited(false);
            setStartDate("");
            setEndDate("");
            setLocation("");
            setModules({ ...MODULE_DEFAULTS });
          }}
          variant="secondary"
          size="sm"
          data-testid="create-another-event"
        >
          <Plus className="h-4 w-4" />
          Create another
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-xl space-y-4" data-testid="events-content">
      <GovernanceCommandFeedback state={cmdState} onReset={reset} />

      <div className="space-y-1.5">
        <Label htmlFor="event-title">Title</Label>
        <Input
          id="event-title"
          placeholder="Connect 2026"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={busy}
          testId="event-title-input"
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="event-id">Event ID</Label>
        <Input
          id="event-id"
          placeholder="connect-2026"
          value={effectiveId}
          onChange={(e) => {
            setIdEdited(true);
            setEventId(e.target.value);
          }}
          disabled={busy}
          testId="event-id-input"
        />
        <p className="text-xs text-muted-foreground">
          Permanent, and part of every URL for this event. Lowercase letters,
          digits and hyphens.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="event-start">Start date</Label>
          <Input
            id="event-start"
            type="date"
            value={startDate}
            onChange={(e) => {
              const value = e.target.value;
              setStartDate(value);
              // An end date before the start is the most common way to fail
              // this form; following the start date along removes it.
              if (endDate && endDate < value) setEndDate(value);
            }}
            disabled={busy}
            testId="event-start-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="event-end">End date</Label>
          <Input
            id="event-end"
            type="date"
            min={startDate || undefined}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            disabled={busy}
            testId="event-end-input"
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="event-location">Location</Label>
        <Input
          id="event-location"
          placeholder="Berlin"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          disabled={busy}
          testId="event-location-input"
        />
      </div>

      <div className="space-y-1.5">
        <Label id="event-timezone-label" htmlFor="event-timezone">
          Timezone
        </Label>
        <TimezonePicker
          id="event-timezone"
          labelId="event-timezone-label"
          testId="event-timezone-select"
          className="w-full"
          value={timezone}
          onChange={setTimezone}
          disabled={busy}
          options={timezoneOptions}
        />
      </div>

      <div className="space-y-2">
        <Label>Sections</Label>
        <p className="text-xs text-muted-foreground">
          Which tabs attendees see. Changeable afterwards on the event&apos;s
          settings page.
        </p>
        <div className="flex flex-wrap gap-1.5">
          {Object.keys(MODULE_DEFAULTS).map((key) => {
            const on = modules[key];
            return (
              <ToggleChip
                key={key}
                selected={on}
                disabled={busy}
                data-testid={`event-module-${key}`}
                onClick={() => setModules({ ...modules, [key]: !on })}
              >
                {MODULE_LABELS[key]}
              </ToggleChip>
            );
          })}
        </div>
        <p
          className="text-xs text-muted-foreground"
          data-testid="event-module-configAttendees-help"
        >
          {ATTENDEES_MODULE_LABEL}: {ATTENDEES_MODULE_HELP}
        </p>
      </div>

      <Button
        variant="cta"
        onClick={handleCreate}
        disabled={!canSubmit}
        data-testid="create-event-btn"
      >
        <CalendarPlus className="h-4 w-4" />
        Create event
      </Button>
    </div>
  );
}

/**
 * Who may administer an event.
 *
 * The only path to `isEventAdmin`: `events/{id}/members` is `write: if false`
 * for every client, so this files a SET_EVENT_ADMIN command and the trigger
 * writes the member document. Until this shipped, an event was permanently
 * administrable by exactly the one person who created it — no delegation, no
 * handover, no recovery if they left.
 *
 * Grant and revoke are the same command with a boolean, so a mis-grant is
 * undone the same way it was made.
 */
function EventAdminsSection({ uid }: { uid: string }) {
  const events = useGovernanceEvents();
  const users = useGovernanceUsers();
  const { state: cmdState, submit, reset } = useGovernanceCommand();

  const [eventId, setEventId] = useState("");
  const [targetUid, setTargetUid] = useState("");
  const admins = useEventAdmins(eventId);
  // After the trigger has applied Make or Remove, not when the command is
  // merely filed, for the same race as every other reload on this page.
  useReloadOnSuccess(cmdState.status, admins.reload);

  const busy =
    cmdState.status === "submitting" || cmdState.status === "watching";
  const selectedEvent =
    events.status === "success"
      ? events.data.find((event) => event.eventId === eventId)
      : undefined;

  const assign = (isEventAdmin: boolean) => {
    if (!eventId || !targetUid) return;
    void submit("SET_EVENT_ADMIN", uid, { eventId, targetUid, isEventAdmin });
  };

  if (events.status === "loading" || users.status === "loading")
    return <LoadingCards cards={3} />;
  if (events.status === "error")
    return (
      <EmptyState icon={AlertCircle} title="Could not load events">
        {describeReadFailure(events.error)}
      </EmptyState>
    );
  if (users.status === "error")
    return (
      <EmptyState icon={AlertCircle} title="Could not load users">
        {describeReadFailure(users.error)}
      </EmptyState>
    );

  if (events.data.length === 0)
    return (
      <EmptyState icon={Shield} title="No events yet">
        Create an event first; then you can choose who administers it.
      </EmptyState>
    );

  return (
    <div className="max-w-xl space-y-4" data-testid="admins-content">
      <GovernanceCommandFeedback state={cmdState} onReset={reset} />

      <div className="space-y-1.5">
        <Label htmlFor="admin-event">Event</Label>
        <Select value={eventId} onValueChange={setEventId} disabled={busy}>
          <SelectTrigger id="admin-event" data-testid="admin-event-select">
            <SelectValue placeholder="Choose an event" />
          </SelectTrigger>
          <SelectContent>
            {events.data.map((event: GovernanceEvent) => (
              <SelectItem key={event.eventId} value={event.eventId}>
                {event.title}
                {event.published === false ? " (unpublished)" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {eventId && (
        <CurrentEventAdmins
          admins={admins}
          users={users.data}
          disabled={busy}
          onRemove={setTargetUid}
        />
      )}

      <div className="space-y-1.5">
        <Label htmlFor="admin-user">Person</Label>
        <Select value={targetUid} onValueChange={setTargetUid} disabled={busy}>
          <SelectTrigger id="admin-user" data-testid="admin-user-select">
            <SelectValue placeholder="Choose a person" />
          </SelectTrigger>
          <SelectContent>
            {users.data.map((user: GovernanceUser) => (
              <SelectItem key={user.uid} value={user.uid}>
                {user.email ?? user.name ?? user.uid}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          Only people who have signed in at least once appear here — the command
          refuses a uid with no profile.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          variant="cta"
          onClick={() => assign(true)}
          disabled={busy || !eventId || !targetUid}
          data-testid="grant-event-admin"
        >
          <Shield className="h-4 w-4" />
          Make event admin
        </Button>
        <Button
          variant="outline"
          onClick={() => assign(false)}
          disabled={busy || !eventId || !targetUid}
          data-testid="revoke-event-admin"
        >
          Remove event admin
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">
        Removing event admin leaves the person registered for the event; it only
        takes away their ability to administer it.
      </p>

      {selectedEvent && (
        <EventDeletionDangerZone
          eventId={selectedEvent.eventId}
          eventTitle={selectedEvent.title ?? selectedEvent.eventId}
          uid={uid}
          onSucceeded={() => {
            setEventId("");
            events.reload();
          }}
        />
      )}
    </div>
  );
}

/**
 * Who administers the chosen event right now, read only.
 *
 * `users` is the list the page has already loaded, so a name and an email cost
 * no read. A uid with no profile is shown by its id and cannot be removed from
 * here, because the command refuses a uid with no profile.
 *
 * "Remove" does not file anything. It puts the person in the Person select, so
 * the same "Remove event admin" button, and its confirmation feedback, is the
 * only path that changes an admin.
 */
function CurrentEventAdmins({
  admins,
  users,
  disabled,
  onRemove,
}: {
  admins: EventAdminsState;
  users: GovernanceUser[];
  disabled: boolean;
  onRemove: (uid: string) => void;
}) {
  const people = useMemo(() => {
    if (admins.status !== "success") return [];
    const byUid = new Map(users.map((user) => [user.uid, user]));
    return admins.uids
      .map((adminUid) => ({ uid: adminUid, user: byUid.get(adminUid) }))
      .sort((a, b) =>
        a.user && b.user
          ? compareUsers(a.user, b.user)
          : a.user
            ? -1
            : b.user
              ? 1
              : a.uid.localeCompare(b.uid),
      );
  }, [admins, users]);

  return (
    <section
      className="space-y-2"
      aria-labelledby="current-admins-title"
      data-testid="current-admins"
    >
      <h3 id="current-admins-title" className="text-sm font-medium">
        Current event admins
        {admins.status === "success" && (
          <span
            className="ml-2 font-normal text-muted-foreground"
            data-testid="current-admins-count"
          >
            {people.length}
          </span>
        )}
      </h3>

      {(admins.status === "loading" || admins.status === "idle") && (
        <p
          className="flex items-center gap-2 text-sm text-muted-foreground"
          role="status"
          data-testid="current-admins-loading"
        >
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading event admins
        </p>
      )}

      {admins.status === "error" && (
        <Notice tone="error" testId="current-admins-error">
          Could not load the event admins. {describeReadFailure(admins.error)}
        </Notice>
      )}

      {admins.status === "success" && admins.unreadable > 0 && (
        <Notice tone="warning" testId="current-admins-unreadable">
          {admins.unreadable === 1
            ? "1 entry could not be read"
            : `${admins.unreadable} entries could not be read`}
          , so this list may be incomplete.
        </Notice>
      )}

      {admins.status === "success" && people.length === 0 && (
        <p
          className="text-sm text-muted-foreground"
          data-testid="current-admins-empty"
        >
          No event admins on record
        </p>
      )}

      {admins.status === "success" && people.length > 0 && (
        <ul className="space-y-2">
          {people.map(({ uid: adminUid, user }) => (
            <li
              key={adminUid}
              className="flex items-center justify-between gap-3 rounded-md border border-border p-3"
              data-testid={`current-admin-${adminUid}`}
            >
              <div className="min-w-0">
                <p className="break-words font-medium text-foreground">
                  {user ? user.name || user.email || adminUid : adminUid}
                </p>
                <p className="break-all text-sm text-muted-foreground">
                  {user
                    ? user.email || "No email on profile"
                    : "No profile for this person"}
                </p>
              </div>
              {user && (
                <Button
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                  disabled={disabled}
                  onClick={() => onRemove(adminUid)}
                  aria-label={`Remove ${user.name || user.email || adminUid}`}
                  data-testid={`current-admin-remove-${adminUid}`}
                >
                  Remove
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function DomainsSection({ uid }: { uid: string }) {
  const domains = useDomainAllowlist();
  const { state: cmdState, submit, reset } = useGovernanceCommand();
  const [newDomain, setNewDomain] = useState("");
  const busy =
    cmdState.status === "submitting" || cmdState.status === "watching";

  useReloadOnSuccess(cmdState.status, domains.reload);

  const handleAdd = async () => {
    // The rules regex accepts lowercase only, so normalize before submitting.
    const domain = newDomain.trim().toLowerCase();
    if (!domain) return;
    setNewDomain("");
    await submit("SET_DOMAIN_ALLOWLIST", uid, { domain, allowed: true });
  };

  // Document IDs are already normalized — domain_allowlist is trigger-written.
  const handleRemove = (domain: string) =>
    submit("SET_DOMAIN_ALLOWLIST", uid, { domain, allowed: false });

  if (domains.status === "loading") return <LoadingCards cards={3} />;
  if (domains.status === "error")
    return (
      <EmptyState icon={AlertCircle} title="Could not load domains">
        {describeReadFailure(domains.error)}
      </EmptyState>
    );

  // A built-in domain stored in the allowlist too is still shown once, as
  // built in: removing its document would change nothing.
  const added = domains.data.filter(
    (domain) => !BUILT_IN_DOMAINS.includes(domain),
  );

  return (
    <div className="space-y-4" data-testid="domains-content">
      <div className="flex items-center justify-between">
        <Badge variant="outline">
          {BUILT_IN_DOMAINS.length + added.length} domains
        </Badge>
      </div>

      <GovernanceCommandFeedback state={cmdState} onReset={reset} />

      <div className="flex gap-2">
        <Input
          placeholder="example.com"
          value={newDomain}
          onChange={(e) => setNewDomain(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleAdd();
          }}
          disabled={busy}
          testId="domain-input"
        />
        <Button
          variant="cta"
          onClick={handleAdd}
          disabled={busy || !newDomain.trim()}
          size="sm"
          data-testid="add-domain-btn"
        >
          <Plus className="h-4 w-4" />
          Add
        </Button>
      </div>

      <div className="space-y-2" data-testid="built-in-domains">
        {BUILT_IN_DOMAINS.map((domain) => (
          <Card key={domain} className="p-4">
            <CardContent className="flex items-center justify-between pt-0">
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-muted-foreground" />
                <span className="font-mono text-sm text-foreground">
                  {domain}
                </span>
              </div>
              <Badge
                variant="outline"
                data-testid={`built-in-domain-${domain}`}
              >
                Built in
              </Badge>
            </CardContent>
          </Card>
        ))}
        <p className="text-xs text-muted-foreground">
          Built-in domains can always sign in, are accepted by attendee imports,
          and register as Internal. They cannot be removed here.
        </p>
      </div>

      {added.length === 0 ? (
        <EmptyState icon={Globe} title="No added domains">
          Only {BUILT_IN_DOMAINS.join(" and ")} can sign in. Add a domain to let
          people from another organisation sign in too.
        </EmptyState>
      ) : (
        <div className="space-y-2" data-testid="added-domains">
          {added.map((domain) => (
            <Card key={domain} className="p-4">
              <CardContent className="flex items-center justify-between gap-2 pt-0">
                <div className="flex min-w-0 items-center gap-2">
                  <Globe className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 font-mono text-sm text-foreground wrap-anywhere">
                    {domain}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="shrink-0"
                  aria-label={`Remove ${domain}`}
                  onClick={() => handleRemove(domain)}
                  disabled={busy}
                  data-testid={`remove-domain-${domain}`}
                >
                  <Trash2 className="h-4 w-4 text-muted-foreground" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function UsersSection({ uid }: { uid: string }) {
  const users = useGovernanceUsers();
  const { state: cmdState, submit, reset } = useGovernanceCommand();
  const [term, setTerm] = useState("");
  const busy =
    cmdState.status === "submitting" || cmdState.status === "watching";

  useReloadOnSuccess(cmdState.status, users.reload);

  const sorted = useMemo(
    () =>
      users.status === "success" ? [...users.data].sort(compareUsers) : [],
    [users],
  );

  // Filtering over the list already in memory. The Users tab loads every
  // users document, which is fine up to the low thousands; past that this
  // wants server-side prefix fields or a search index.
  const visible = useMemo(() => {
    const matches = searchMatcher(term);
    return sorted.filter((user) => matches(userSearchFields(user)));
  }, [sorted, term]);

  const handleRoleChange = (targetUid: string, newRole: string) =>
    submit("SET_PLATFORM_ROLE", uid, { targetUid, newRole });

  const handleDelete = (targetUid: string) =>
    submit("DELETE_USER", uid, { targetUid });

  if (users.status === "loading") return <LoadingCards cards={4} />;
  if (users.status === "error")
    return (
      <EmptyState icon={AlertCircle} title="Could not load users">
        {describeReadFailure(users.error)}
      </EmptyState>
    );

  return (
    <div className="space-y-4" data-testid="users-content">
      <div className="flex items-center justify-between">
        <Badge variant="outline" aria-live="polite" data-testid="user-count">
          {visible.length} of {users.data.length} users
        </Badge>
      </div>

      <GovernanceCommandFeedback state={cmdState} onReset={reset} />

      <Input
        type="search"
        placeholder="Search name, email, company, job title or role"
        aria-label="Search users"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape" && term) {
            e.preventDefault();
            setTerm("");
          }
        }}
        disabled={busy}
        testId="user-search"
        className="sm:max-w-md"
      />

      {users.data.length === 0 ? (
        <EmptyState icon={Users} title="No users">
          No users have been provisioned yet.
        </EmptyState>
      ) : visible.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No users match"
          testId="users-no-match"
          action={
            <Button
              variant="outline"
              className="h-11 sm:h-10"
              onClick={() => setTerm("")}
              data-testid="clear-user-search"
            >
              Clear search
            </Button>
          }
        >
          No user matches &ldquo;{term.trim()}&rdquo;. Check the spelling or
          search for fewer words.
        </EmptyState>
      ) : (
        <div className="space-y-2">
          {visible.map((user: GovernanceUser) => (
            <Card
              key={user.uid}
              className="p-4"
              data-testid={`user-row-${user.uid}`}
            >
              <CardContent className="flex flex-col gap-3 pt-0 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className="font-medium text-foreground">
                    {user.name ?? user.email ?? user.uid}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {user.email ?? "No email on profile"}
                    {user.uid === uid && " — you"}
                  </p>
                  {(user.jobTitle || user.company) && (
                    <p className="text-sm text-muted-foreground">
                      {[user.jobTitle, user.company].filter(Boolean).join(", ")}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Select
                    value={user.platformRole ?? "user"}
                    onValueChange={(role) => handleRoleChange(user.uid, role)}
                    // Changing your own role would revoke access to this page,
                    // and the stale custom claim hides that until token refresh.
                    disabled={busy || user.uid === uid}
                  >
                    <SelectTrigger
                      className="h-11 flex-1 sm:h-10 sm:w-36 sm:flex-none"
                      testId={`role-select-${user.uid}`}
                      ariaLabel={`Platform role for ${user.name ?? user.email ?? user.uid}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">{ROLE_LABELS.user}</SelectItem>
                      <SelectItem value="admin">{ROLE_LABELS.admin}</SelectItem>
                      <SelectItem value="super_admin">
                        {ROLE_LABELS.super_admin}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <UserDeletionControl
                    user={user}
                    // Self-deletion is refused by the trigger too; disabling it
                    // here just avoids offering an action that cannot succeed.
                    disabled={busy || user.uid === uid}
                    onConfirm={() => handleDelete(user.uid)}
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Deleting an account is not the same as erasing a person from an event.
 *
 * The dialog says so explicitly, because the two are easy to confuse and the
 * backend deliberately keeps the attendee record: it is keyed by a hash of the
 * email, belongs to the event, and exists for imported people who never signed
 * in at all. What goes is the account and everything keyed on its uid.
 *
 * It does not stop the person coming back. The uid is retired, but an address
 * that still passes the sign-in policy creates a new account with a new uid on
 * the next sign-in, and the roster rows keep `accountDeletedAt`, so that account
 * starts empty. The wording says that rather than promising a ban.
 */
function UserDeletionControl({
  user,
  disabled,
  onConfirm,
}: {
  user: GovernanceUser;
  disabled: boolean;
  onConfirm: () => void;
}) {
  const [open, setOpen] = useState(false);
  const label = user.name ?? user.email ?? user.uid;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        variant="ghost"
        size="sm"
        className="h-11 w-11 sm:h-9 sm:w-auto"
        disabled={disabled}
        aria-label={`Delete ${label}`}
        data-testid={`delete-user-${user.uid}`}
        onClick={() => setOpen(true)}
      >
        <Trash2 className="h-4 w-4 text-destructive-text" />
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete {label}?</DialogTitle>
          <DialogDescription>
            This permanently deletes their account, profile, platform role,
            event access, bookmarks and photo. Attendee records stay on their
            events, with the link to this account removed. If they sign in again
            later, they get a new, empty account. This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            data-testid={`confirm-delete-user-${user.uid}`}
            onClick={() => {
              setOpen(false);
              onConfirm();
            }}
          >
            Permanently delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BusinessUnitsSection() {
  const units = useBusinessUnits();
  const writes = useBusinessUnitWrites();
  const [editor, setEditor] = useState<
    GovernanceBusinessUnit | null | undefined
  >(undefined);
  const [deleting, setDeleting] = useState<GovernanceBusinessUnit | null>(null);
  const busy = writes.state.status === "submitting";

  const save = async (name: string, head: string) => {
    const succeeded =
      editor && editor.id
        ? await writes.update(editor.id, name, head)
        : await writes.create(name, head);
    if (succeeded) {
      setEditor(undefined);
      units.reload();
    }
  };

  const remove = async () => {
    if (!deleting) return;
    const succeeded = await writes.remove(deleting.id);
    if (succeeded) {
      setDeleting(null);
      units.reload();
    }
  };

  if (units.status === "loading") return <LoadingCards cards={3} />;
  if (units.status === "error")
    return (
      <EmptyState icon={AlertCircle} title="Could not load business units">
        {units.error}
      </EmptyState>
    );

  return (
    <div className="space-y-4" data-testid="units-content">
      <div className="flex items-center justify-between">
        <Badge variant="outline">{units.data.length} units</Badge>
        <Button
          variant="cta"
          size="sm"
          onClick={() => {
            writes.reset();
            setEditor(null);
          }}
        >
          <Plus className="h-4 w-4" />
          Add business unit
        </Button>
      </div>

      {writes.state.status === "error" &&
        editor === undefined &&
        deleting === null && (
          <Notice
            tone="error"
            action={
              <button
                type="button"
                onClick={writes.reset}
                className="flex shrink-0 items-center justify-center rounded-sm hover:opacity-80 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring max-sm:-my-2 max-sm:-mr-2 max-sm:size-11"
                aria-label="Dismiss business unit error"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            }
          >
            {describeReadFailure(writes.state.error)}
          </Notice>
        )}

      {units.data.length === 0 ? (
        <EmptyState icon={Building2} title="No business units">
          No business units have been configured yet.
        </EmptyState>
      ) : (
        <div className="space-y-2">
          {units.data.map((u) => (
            <Card key={u.id} className="p-4">
              <CardContent className="flex items-center justify-between gap-3 pt-0">
                <div className="min-w-0">
                  <p className="font-medium text-foreground">{u.name}</p>
                  {u.head && (
                    <p className="mt-0.5 text-sm text-muted-foreground">
                      Head: {u.head}
                    </p>
                  )}
                  {u.validationError && (
                    <p className="mt-1 text-xs text-destructive-text">
                      Stored data is invalid ({u.validationError}). Edit or
                      delete this unit.
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      writes.reset();
                      setEditor(u);
                    }}
                    disabled={busy}
                    aria-label={`Edit ${u.name}`}
                    data-testid={`edit-unit-${u.id}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      writes.reset();
                      setDeleting(u);
                    }}
                    disabled={busy}
                    aria-label={`Delete ${u.name}`}
                    data-testid={`delete-unit-${u.id}`}
                  >
                    <Trash2 className="h-4 w-4 text-destructive-text" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog
        open={editor !== undefined}
        onOpenChange={(open) => {
          if (!open && !busy) setEditor(undefined);
        }}
      >
        {editor !== undefined && (
          <BusinessUnitEditor
            key={editor?.id ?? "new"}
            unit={editor}
            busy={busy}
            existingNames={units.data
              .filter((unit) => unit.id !== editor?.id)
              .map((unit) => unit.name)}
            error={
              writes.state.status === "error"
                ? describeReadFailure(writes.state.error)
                : undefined
            }
            onCancel={() => setEditor(undefined)}
            onSave={save}
          />
        )}
      </Dialog>

      <Dialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open && !busy) setDeleting(null);
        }}
      >
        {deleting && (
          <DialogContent className="w-[min(32rem,calc(100vw-2rem))]">
            <DialogHeader>
              <DialogTitle>Delete {deleting.name}?</DialogTitle>
              <DialogDescription>
                Existing attendee profiles keep their current company value.
                This only removes the unit from future selections.
              </DialogDescription>
            </DialogHeader>
            {writes.state.status === "error" && (
              <BusinessUnitWriteError
                error={describeReadFailure(writes.state.error)}
              />
            )}
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDeleting(null)}
                disabled={busy}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={remove}
                disabled={busy}
                data-testid="confirm-delete-unit"
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                Delete business unit
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}

function BusinessUnitEditor({
  unit,
  busy,
  existingNames,
  error,
  onCancel,
  onSave,
}: {
  unit: GovernanceBusinessUnit | null;
  busy: boolean;
  existingNames: string[];
  error?: string;
  onCancel: () => void;
  onSave: (name: string, head: string) => Promise<void>;
}) {
  const [name, setName] = useState(unit?.name ?? "");
  const [head, setHead] = useState(unit?.head ?? "");
  const normalizedName = name.trim();
  const normalizedHead = head.trim();
  const duplicateName = existingNames.includes(normalizedName);
  // The head is optional: the collection is the company vocabulary the attendee
  // and profile pickers read, and the standard units are seeded with nobody
  // named against them.
  const valid =
    normalizedName.length > 0 &&
    normalizedName.length <= 200 &&
    normalizedHead.length <= 200 &&
    !duplicateName;

  return (
    <DialogContent className="w-[min(32rem,calc(100vw-2rem))]">
      <DialogHeader>
        <DialogTitle>
          {unit ? "Edit business unit" : "Add business unit"}
        </DialogTitle>
        <DialogDescription>
          Business units provide the company vocabulary used by attendee
          profiles.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        {error && <BusinessUnitWriteError error={error} />}
        {duplicateName && (
          <BusinessUnitWriteError
            error={`A business unit named ${normalizedName} already exists.`}
          />
        )}
        <div className="space-y-2">
          <Label htmlFor="business-unit-name">Name</Label>
          <Input
            id="business-unit-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={200}
            data-testid="business-unit-name"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="business-unit-head">Head</Label>
          <Input
            id="business-unit-head"
            value={head}
            onChange={(event) => setHead(event.target.value)}
            maxLength={200}
            data-testid="business-unit-head"
          />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="cta"
          onClick={() => onSave(normalizedName, normalizedHead)}
          disabled={busy || !valid}
          data-testid="save-business-unit"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {unit ? "Save changes" : "Create business unit"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function BusinessUnitWriteError({ error }: { error: string }) {
  return <Notice tone="error">{error}</Notice>;
}
